import { styles } from "@repo/helpers";
import { useMemo } from "react";

import type { ApplicationSetting } from "../../../models";
import type { DefinitionTree, Segment, SettingGridRow } from "../settingsTree";

import { settingFamily, settingRow, valueLevel } from "../settingsTree";
import { ChainLevel } from "./chainLevel";
import style from "./style.module.scss";

interface Props {
	tree: DefinitionTree;
	settingsByName: Map<string, ApplicationSetting[]>;
	/** The opened setting */
	row: SettingGridRow;
	/** The segment picked in the filter bar, so each level's value matches the grid */
	segment?: Segment;
	onOpen: (name: string) => void;
}

/**
 * The opened setting's place in the hierarchy: the direct chain from the top level down to it, then its direct children.
 * Arrows mark where each level overrides the one above or is inherited from it, and the level that gives the value is
 * highlighted. Nothing for a setting the code reads by its full name only
 */
export function SettingChain({ tree, settingsByName, row, segment, onOpen }: Props) {
	const family = useMemo(() => {
		const { above, children } = settingFamily(tree, settingsByName, row.name);
		const toRow = (name: string) => settingRow(tree, settingsByName, name, undefined, segment);
		return { above: above.map(toRow), children: children.map(toRow) };
	}, [tree, settingsByName, row.name, segment]);

	if (!family.above.length && !family.children.length) return null;
	const chain = [...family.above, row];
	const active = valueLevel(
		row,
		chain.map((level) => level.name),
	);

	return (
		<section className={style.detailsSection}>
			<h4 className={style.sectionTitle}>Hierarchy</h4>
			<ol className={style.chainList}>
				{chain.map((level, i) => (
					<li key={level.key}>
						<ChainLevel
							row={level}
							arrow={i > 0 ? "chain" : undefined}
							active={level.key === active}
							current={level === row}
							onOpen={onOpen}
						/>
					</li>
				))}
			</ol>
			{family.children.length > 0 && (
				<>
					<p className={style.hint}>
						Direct children, which read this setting when they have no row that applies
					</p>
					<ul className={styles(style.chainList, style.chainChildren)}>
						{family.children.map((child) => (
							<li key={child.key}>
								<ChainLevel row={child} arrow="child" onOpen={onOpen} />
							</li>
						))}
					</ul>
				</>
			)}
		</section>
	);
}
